import { pdfjs } from "react-pdf";
import mammoth from "mammoth";
import * as XLSX from "xlsx";
import getWorkerScriptPath from "../ElectronUtils";

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = getWorkerScriptPath();

// Unsupported file types for local Ollama processing (legacy binary formats)
const UNSUPPORTED_EXTENSIONS = ["doc", "xls", "ppt"];

// Text-based file extensions that can be read directly
const TEXT_EXTENSIONS = [
  "txt",
  "md",
  "json",
  "csv",
  "html",
  "xml",
  "js",
  "ts",
  "tsx",
  "jsx",
  "py",
  "java",
  "css",
  "sql",
  "sh",
  "yaml",
  "yml",
  "rb",
  "php",
  "go",
  "rs",
  "swift",
  "kt",
  "c",
  "cpp",
  "h",
  "hpp",
];

/**
 * Extract text from file locally (for Ollama integration)
 * @param {File|Blob} file - The file to process
 * @param {string} fileName - File name with extension
 * @returns {Promise<string>} - Extracted text content
 * @throws {Error} - If file type is not supported for local processing
 */
export const extractTextFromFile = async (file, fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();

  // Throw error for unsupported types (DOCX, XLSX, etc.)
  if (UNSUPPORTED_EXTENSIONS.includes(ext)) {
    throw new Error(
      `File type .${ext} is not supported for local Ollama processing. Use cloud AI or convert to PDF/TXT.`
    );
  }

  // Plain text files - read directly
  if (TEXT_EXTENSIONS.includes(ext)) {
    return await file.text();
  }

  // PDF - extract text using pdfjs
  if (ext === "pdf") {
    return await extractPdfText(file);
  }

  // DOCX - extract text using mammoth
  if (ext === "docx") {
    return await extractDocxText(file);
  }

  // XLSX - extract text using xlsx
  if (ext === "xlsx") {
    return await extractXlsxText(file);
  }

  // PPTX - extract text
  if (ext === "pptx") {
    return await extractPptxText(file);
  }

  // Unknown type - return placeholder
  return `[File: ${fileName} - content extraction not supported]`;
};

/**
 * Extract text from PDF using pdfjs-dist
 * @param {File|Blob} file - The PDF file
 * @returns {Promise<string>} - Extracted text
 */
const extractPdfText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;

  let fullText = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const pageText = content.items.map((item) => item.str).join(" ");
    fullText += pageText + "\n";
  }
  return fullText.trim();
};

/**
 * Extract text from DOCX using mammoth
 * @param {File|Blob} file - The DOCX file
 * @returns {Promise<string>} - Extracted text
 */
const extractDocxText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value.trim();
};

/**
 * Extract text from XLSX using xlsx (SheetJS)
 * @param {File|Blob} file - The XLSX file
 * @returns {Promise<string>} - Extracted text (all sheets)
 */
const extractXlsxText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: "array" });

  let fullText = "";
  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    // Convert to CSV for readable text extraction
    const csv = XLSX.utils.sheet_to_csv(sheet);
    fullText += `--- Sheet: ${sheetName} ---\n${csv}\n\n`;
  });
  return fullText.trim();
};

/**
 * Extract text from PPTX
 * PPTX files are ZIP archives containing XML files
 * @param {File|Blob} file - The PPTX file
 * @returns {Promise<string>} - Extracted text from all slides
 */
const extractPptxText = async (file) => {
  const arrayBuffer = await file.arrayBuffer();
  const JSZip = (await import("jszip")).default;
  const zip = await JSZip.loadAsync(arrayBuffer);

  const slideTexts = [];
  const slideFiles = Object.keys(zip.files)
    .filter((name) => name.match(/ppt\/slides\/slide\d+\.xml$/))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)\.xml$/)[1]);
      const numB = parseInt(b.match(/slide(\d+)\.xml$/)[1]);
      return numA - numB;
    });

  for (const slidePath of slideFiles) {
    const slideXml = await zip.file(slidePath).async("string");
    // Extract text content from XML (simplified regex-based extraction)
    const textMatches = slideXml.match(/<a:t>([^<]*)<\/a:t>/g) || [];
    const slideText = textMatches
      .map((match) => match.replace(/<a:t>([^<]*)<\/a:t>/, "$1"))
      .join(" ");
    if (slideText.trim()) {
      const slideNum = slidePath.match(/slide(\d+)\.xml$/)[1];
      slideTexts.push(`--- Slide ${slideNum} ---\n${slideText.trim()}`);
    }
  }

  return slideTexts.join("\n\n");
};

/**
 * Convert image URL to base64 string (for Ollama vision)
 * @param {string} url - The image URL (e.g., presigned S3 URL)
 * @returns {Promise<string>} - Base64 encoded string (without data URI prefix)
 */
export const imageUrlToBase64 = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove the data URI prefix (e.g., "data:image/png;base64,")
      const base64 = reader.result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to convert image to base64"));
    reader.readAsDataURL(blob);
  });
};

/**
 * Convert multiple image URLs to base64 array
 * @param {string[]} urls - Array of image URLs
 * @returns {Promise<string[]>} - Array of base64 encoded strings
 */
export const imageUrlsToBase64Array = async (urls) => {
  if (!urls || urls.length === 0) return [];
  return Promise.all(urls.map(imageUrlToBase64));
};
